import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchPerson,
  fetchPersonBooks,
  deletePerson,
  updateBookAuthorBio,
  BookWithRole,
} from "@/api/people";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Trash,
  BookOpen,
  Mic,
  Languages,
  Mail,
  Calendar,
  User,
  Save,
  X,
  Pencil,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

// Colors for role badges - match the role indicator icon colors
const getRoleBadgeColor = (roleName: string): string => {
  switch (roleName.toLowerCase()) {
    case "author":
      return "bg-blue-500/10 text-blue-600";
    case "narrator":
      return "bg-green-500/10 text-green-600";
    case "translator":
      return "bg-amber-500/10 text-amber-600";
    default:
      return "bg-primary/10 text-primary";
  }
};

const PersonDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const personId = Number(id);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingBioBookId, setEditingBioBookId] = useState<number | null>(null);
  const [editBioValue, setEditBioValue] = useState("");

  const {
    data: person,
    isLoading: isLoadingPerson,
    error: personError,
  } = useQuery({
    queryKey: ["person", personId],
    queryFn: () => fetchPerson(personId),
    enabled: !!personId,
  });

  const {
    data: personBooks,
    isLoading: isLoadingBooks,
  } = useQuery({
    queryKey: ["personBooks", personId],
    queryFn: () => fetchPersonBooks(personId),
    enabled: !!personId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePerson(personId),
    onSuccess: () => {
      toast({
        title: "Person deleted",
        description: `${person?.name} has been deleted successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ["people"] });
      navigate("/admin/people");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while deleting the person",
        variant: "destructive",
      });
    },
  });

  const bioMutation = useMutation({
    mutationFn: ({
      bookId,
      bio,
    }: {
      bookId: number;
      bio: string | null;
    }) => updateBookAuthorBio(bookId, personId, bio),
    onSuccess: () => {
      toast({ title: "Bio updated", description: "Per-book bio saved successfully." });
      queryClient.invalidateQueries({ queryKey: ["personBooks", personId] });
      setEditingBioBookId(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to update bio",
        variant: "destructive",
      });
    },
  });

  const handleStartEditBio = (book: BookWithRole) => {
    setEditingBioBookId(book.id);
    setEditBioValue(book.bookBio || "");
  };

  const handleSaveBio = (bookId: number) => {
    const trimmed = editBioValue.trim();
    bioMutation.mutate({ bookId, bio: trimmed || null });
  };

  const handleCancelEditBio = () => {
    setEditingBioBookId(null);
    setEditBioValue("");
  };

  if (isLoadingPerson) {
    return (
      <AdminLayout title="Person Details">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (personError || !person) {
    return (
      <AdminLayout title="Person Details">
        <div className="text-center py-20">
          <p className="text-muted-foreground">Person not found.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/admin/people")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to People
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const bookCount = personBooks?.length || 0;

  return (
    <AdminLayout
      title={person.name}
      actionButton={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/people/${personId}/edit`)}
          >
            <Edit className="mr-2 h-4 w-4" /> Edit Person
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash className="mr-2 h-4 w-4" /> Delete
          </Button>
        </div>
      }
    >
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate("/admin/people")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to People
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side — 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          {/* Global Bio Card */}
          {person.bio && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Bio</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {person.bio}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Associated Books Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Associated Books ({bookCount})
              </CardTitle>
              <CardDescription>
                Books where this person is credited as author, narrator, or
                translator
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingBooks ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : personBooks && personBooks.length > 0 ? (
                <div className="space-y-4">
                  {personBooks.map((book) => (
                    <div
                      key={book.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              className="font-semibold text-sm hover:underline text-left"
                              onClick={() =>
                                navigate(`/admin/books/${book.id}`)
                              }
                            >
                              {book.title}
                            </button>
                            {/* Role badges */}
                            <div className="flex gap-1">
                              {book.roleTypes.isAuthor && (
                                <Badge
                                  variant="outline"
                                  className="bg-blue-500/10 text-blue-600 text-xs"
                                >
                                  <BookOpen className="h-3 w-3 mr-1" />
                                  Author
                                </Badge>
                              )}
                              {book.roleTypes.isNarrator && (
                                <Badge
                                  variant="outline"
                                  className="bg-green-500/10 text-green-600 text-xs"
                                >
                                  <Mic className="h-3 w-3 mr-1" />
                                  Narrator
                                </Badge>
                              )}
                              {book.roleTypes.isTranslator && (
                                <Badge
                                  variant="outline"
                                  className="bg-amber-500/10 text-amber-600 text-xs"
                                >
                                  <Languages className="h-3 w-3 mr-1" />
                                  Translator
                                </Badge>
                              )}
                            </div>
                          </div>
                          {book.authors && book.authors.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              by{" "}
                              {book.authors.map((a) => a.name).join(", ")}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Per-book bio (author only) */}
                      {book.roleTypes.isAuthor && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-muted-foreground">
                              Author bio for this book
                            </span>
                            {editingBioBookId !== book.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleStartEditBio(book)}
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                {book.bookBio ? "Edit" : "Add bio"}
                              </Button>
                            )}
                          </div>
                          {editingBioBookId === book.id ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editBioValue}
                                onChange={(e) =>
                                  setEditBioValue(e.target.value)
                                }
                                placeholder="Enter per-book author bio..."
                                rows={3}
                                className="text-sm"
                              />
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="h-7"
                                  onClick={() => handleSaveBio(book.id)}
                                  disabled={bioMutation.isPending}
                                >
                                  <Save className="h-3 w-3 mr-1" />
                                  Save
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7"
                                  onClick={handleCancelEditBio}
                                  disabled={bioMutation.isPending}
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : book.bookBio ? (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {book.bookBio}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground/60 italic">
                              No per-book bio set
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No books associated with this person.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right side — 1 col */}
        <div className="space-y-6">
          {/* Profile Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Profile</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center text-center">
              <Avatar className="h-20 w-20 mb-3">
                <AvatarImage src={person.avatarUrl} />
                <AvatarFallback className="text-2xl">
                  {person.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <h3 className="font-semibold text-lg">{person.name}</h3>
              {person.email && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                  <Mail className="h-3 w-3" />
                  {person.email}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Roles Card */}
          {person.roles.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Roles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {person.roles.map((role) => (
                    <Badge
                      key={role.id}
                      className={cn(
                        getRoleBadgeColor(role.name),
                        "font-medium px-2 py-0.5 text-xs"
                      )}
                      variant="outline"
                    >
                      {role.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <BookOpen className="h-3 w-3" /> Books
                </span>
                <span className="font-medium">{bookCount}</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Created
                </span>
                <span className="font-medium">
                  {person.createdAt
                    ? format(new Date(person.createdAt), "MMM d, yyyy")
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Updated
                </span>
                <span className="font-medium">
                  {person.updatedAt
                    ? format(new Date(person.updatedAt), "MMM d, yyyy")
                    : "—"}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Person</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {person.name}? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default PersonDetails;
